import { Box, Modal, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, Paper } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/toast-context";
import { ScheduleService } from "../../services/schedule-service";
import { EmployeeScheduleDto } from "../../services/dto/response/schedule-response-dto";
import { LoaderBounce } from "../LoaderBounce";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50vw",
  maxWidth: "800px",
  maxHeight: "80vh",
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: 4,
};

interface AssignEmployeesModalProps {
  isOpen: boolean;
  onClose: () => void;
  scheduleId: number;
  onAssignEmployees?: (scheduleId: number, employeeIds: number[]) => void;
}

export function AssignEmployeesModal({
  isOpen,
  onClose,
  scheduleId,
  onAssignEmployees,
}: AssignEmployeesModalProps) {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<EmployeeScheduleDto[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { showToast } = useToast();
  const scheduleService = useMemo(() => new ScheduleService(), []);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      scheduleService.getEmployeesByScheduleId(
        { scheduleId },
        (data) => {
          setEmployees(data);
          setSelectedEmployees(
            data.filter(e => e.workSchedules.some(ws => ws.workScheduleId === scheduleId))
                .map(e => e.employeeBranchId)
          );
        },
        (error) => showToast(error, "error")
      ).finally(() => setIsLoading(false));
    }
  }, [isOpen, scheduleId, showToast, scheduleService]);

  const handleEmployeeToggle = (employeeBranchId: number) => {
    setSelectedEmployees((prev) => {
      if (prev.includes(employeeBranchId)) {
        return prev.filter((id) => id !== employeeBranchId);
      } else {
        return [...prev, employeeBranchId];
      }
    });
  };

  const handleSave = async () => {
    setIsLoading(true);
    await scheduleService.assignEmployeesToSchedule(
      {
        workScheduleId: scheduleId,
        employeeBranchIds: selectedEmployees
      },
      () => {
        if (onAssignEmployees) {
          onAssignEmployees(scheduleId, selectedEmployees);
        }
        showToast(t("branches.schedules.assign-employees.success"), "success");
        onClose();
      },
      (error) => showToast(error, "error")
    ).finally(() => setIsLoading(false));
  };

  const allSelected = employees.length > 0 && selectedEmployees.length === employees.length;
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(e => e.employeeBranchId));
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="assign-employees-modal"
      aria-describedby="assign-employees-modal"
    >
      <Box sx={style}>
        <div className="bg-gray-light text-center text-xl font-normal text-black py-8 px-2 border-b border-gray-200">
          <h2 className="font-montserrat font-medium">
            {t("branches.schedules.assign-employees.title")}
          </h2>
        </div>

        <div className="p-6">
          <div className="flex justify-end mb-2">
            <button
              onClick={toggleSelectAll}
              className="px-3 py-1 bg-[#EF0BB8] text-white rounded hover:bg-[#d009a6] font-montserrat text-sm"
            >
              {allSelected
                ? t("branches.schedules.assign-employees.deselect-all")
                : t("branches.schedules.assign-employees.select-all")}
            </button>
          </div>
          {isLoading ? (
            <div className="w-full h-full flex justify-center items-center py-8">
              <LoaderBounce />
            </div>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: "none", overflowY: "auto", maxHeight: "40vh" }} className="custom-scrollbar">
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ fontFamily: "Montserrat", fontWeight: 600 }}
                    >
                      {t("branches.schedules.assign-employees.assigned")}
                    </TableCell>
                    <TableCell
                      sx={{ fontFamily: "Montserrat", fontWeight: 600 }}
                    >
                      {t("branches.schedules.assign-employees.employee")}
                    </TableCell>
                    <TableCell
                      sx={{ fontFamily: "Montserrat", fontWeight: 600 }}
                    >
                      {t("branches.schedules.assign-employees.email")}
                    </TableCell>
                    <TableCell
                      sx={{ fontFamily: "Montserrat", fontWeight: 600 }}
                    >
                      {t("branches.schedules.assign-employees.templates")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((employee) => (
                    <TableRow
                      key={employee.employeeBranchId}
                      hover
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedEmployees.includes(
                            employee.employeeBranchId
                          )}
                          onChange={() =>
                            handleEmployeeToggle(employee.employeeBranchId)
                          }
                          sx={{
                            color: "#EF0BB8",
                            "&.Mui-checked": { color: "#EF0BB8" },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Montserrat" }}>
                        {employee.name}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Montserrat" }}>
                        {employee.email}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Montserrat" }}>
                        {employee.workSchedules
                          .map((ws) => ws.name)
                          .join(", ") || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!isLoading && (
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-montserrat"
              >
                {t("cancel")}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-[#EF0BB8] text-white rounded-lg hover:bg-[#d009a6] font-montserrat"
              >
                {t("save")}
              </button>
            </div>
          )}
        </div>
      </Box>
    </Modal>
  );
} 
